import * as B from '@blueprintjs/core';
import * as I from 'immutable';
import React from 'react';
import {Column, TableInstance} from 'react-table';

import ActiveFiltersCountButton from 'app/components/ActiveFiltersCountButton/ActiveFiltersCountButton';
import {useAlertPolicies} from 'app/components/Alerts/AlertPolicyProvider';
import List, {ListItem} from 'app/components/List';
import {ApiFeatureCollection} from 'app/modules/Remote/FeatureCollection';
import {ApiOrganization, ApiOrganizationUser} from 'app/modules/Remote/Organization';
import featureFlags from 'app/utils/featureFlags';
import * as hookUtils from 'app/utils/hookUtils';
import * as tagUtils from 'app/utils/tagUtils';

import type {ImageryFilterValue, NoteCountFilterValue, TagFilterValue} from './ColumnFilters';
import {
  decodeAlertCountFilterValue,
  decodeTagFilterValue,
  encodeAlertCountFilterValue,
  encodeTagFilterValue,
} from './ColumnFilters';
import {
  PropertyFilterOptions,
  TagFilterContent,
  assigneeFilterBaseListItems,
  countActivePropertyOverviewFilters,
  dateFilterListItems,
  imageryFilterListItems,
  isPropertyOverviewFilterActive,
  mapAllAnyNoneMenuIdToFilterValue,
  mapNoteCountMenuIdToFilterValue,
  noteCountFilterListItems,
  shouldResetTagFilter,
} from './filterUtils';
import csFilters from './PropertyOverview.module.scss';
import type {PropertyTableRow} from './PropertyTableTypes';
import {getAlertListItems, makeUserListItems} from './utils';

export const AdditionalFeaturePropertyFilterMenuBody: React.FunctionComponent<{
  column: Column<PropertyTableRow>;
  table: TableInstance<PropertyTableRow>;
}> = ({column, table}) => {
  const columnId = column.id as string;
  const rawValue = table.state.filters.find((f) => f.id === columnId)?.value;

  const presenceValue: PropertyFilterOptions =
    rawValue === 'any' || rawValue === 'none' ? rawValue : 'contains';

  const textValue = !!rawValue && presenceValue === 'contains' ? rawValue : '';

  return (
    <div className={csFilters.actionMenu}>
      <div className={csFilters.additionalFeaturePropertyFilterMenu}>
        <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
          <B.Label style={{margin: 0, flex: 1}}>{columnId}</B.Label>
          <B.Tooltip content="Clear filter">
            <B.Button
              minimal
              icon="cross"
              onClick={() => {
                table.setFilter(columnId, undefined);
              }}
            />
          </B.Tooltip>
        </div>
        <B.RadioGroup
          selectedValue={presenceValue ?? undefined}
          onChange={(e: React.FormEvent<HTMLInputElement>) => {
            const v = e.currentTarget.value as PropertyFilterOptions;
            switch (v) {
              case 'any':
              case 'none':
                table.setFilter(columnId, v);
                return;
              case 'contains':
                table.setFilter(columnId, '');
                return;
            }
          }}
        >
          <B.Radio label="Has any value" value="any" />
          <B.Radio label="No value" value="none" />
          <B.Radio label="Contains value:" value="contains" />
        </B.RadioGroup>
        <B.InputGroup
          leftIcon="search"
          fill
          disabled={presenceValue !== 'contains'}
          placeholder="Contains text..."
          value={textValue}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            const v = e.target.value;
            table.setFilter(columnId, v === '' ? '' : v);
          }}
        />
      </div>
    </div>
  );
};

export interface Props {
  organization: I.ImmutableOf<ApiOrganization>;
  organizationUsers: ApiOrganizationUser[];
  table: TableInstance<PropertyTableRow>;
  selectedFeatureCollection: I.ImmutableOf<ApiFeatureCollection>;
  isHiddenImageryShownAsAvailable: boolean;
  setIsHiddenImageryShownAsAvailable: (show: boolean) => void;
  additionalFeatureProperties: I.Set<string>;
}

const PropertyOverviewFiltersMenu: React.FunctionComponent<React.PropsWithChildren<Props>> = ({
  organization,
  organizationUsers,
  table,
  selectedFeatureCollection,
  isHiddenImageryShownAsAvailable,
  setIsHiddenImageryShownAsAvailable,
  additionalFeatureProperties,
}) => {
  const tagSettings = hookUtils.useContinuity(
    tagUtils.getTagSettings(organization, tagUtils.TAG_KIND.FEATURE),
    I.is
  );

  const {alertPolicies} = useAlertPolicies();

  const tagsColumn = table.columns.find((col) => col.id === 'tags');
  const imageryColumn = table.columns.find((col) => col.id === 'imagery');
  const notesColumn = table.columns.find((col) => col.id === 'userNoteCount');
  const alertColumn = table.columns.find((col) => col.id === 'alertNoteCount');
  const assigneeColumn = table.columns.find((col) => col.id === 'assignee');
  const dateColumn = table.columns.find((col) => col.id === 'reportingDueDate');
  const additionalFeaturePropertyColumns = table.columns.filter(
    (col) => col.id && additionalFeatureProperties.has(col.id)
  );

  // Count active filters
  const activeFilterCount = React.useMemo(() => {
    return countActivePropertyOverviewFilters({
      filters: table.state.filters as any,
      tagSettings,
      additionalFeaturePropertyColumnIds: additionalFeatureProperties,
    });
  }, [table.state.filters, tagSettings, additionalFeatureProperties]);

  const isFilterActive = React.useCallback(
    (columnId: string) => {
      const filter = table.state.filters.find((f) => f.id === columnId);
      return isPropertyOverviewFilterActive({
        columnId,
        rawValue: filter?.value,
        tagSettings,
        additionalFeaturePropertyColumnIds: additionalFeatureProperties,
      });
    },
    [table.state.filters, tagSettings, additionalFeatureProperties]
  );

  const renderTagsFilterMenuItem = () => {
    if (!tagsColumn) return null;

    const isActive = isFilterActive('tags');
    const tagValue = decodeTagFilterValue(tagsColumn.filterValue, tagSettings);
    const setTagValue = (v: TagFilterValue | undefined) => {
      return table.setFilter(
        'tags',
        !shouldResetTagFilter(v) && v ? encodeTagFilterValue(v) : undefined
      );
    };

    return (
      <B.MenuItem
        key="tags"
        text="Tags"
        icon={isActive ? 'filter' : 'blank'}
        active={isActive}
        shouldDismissPopover={false}
      >
        <TagFilterContent tagSettings={tagSettings} value={tagValue} setValue={setTagValue} />
      </B.MenuItem>
    );
  };

  const renderImageryFilterMenuItem = () => {
    if (!imageryColumn) return null;

    const isActive = isFilterActive('imagery');
    const value: ImageryFilterValue = imageryColumn.filterValue || 'all';

    return (
      <B.MenuItem
        key="imagery"
        text="High-Res Imagery"
        icon={isActive ? 'filter' : 'blank'}
        active={isActive}
        shouldDismissPopover={false}
      >
        <List
          items={imageryFilterListItems(value, isHiddenImageryShownAsAvailable)}
          onClickItem={({id}) => {
            if (id === 'hidden') {
              setIsHiddenImageryShownAsAvailable(!isHiddenImageryShownAsAvailable);
            } else {
              table.setFilter(imageryColumn.id, id === 'all' ? undefined : id);
            }
          }}
        />
      </B.MenuItem>
    );
  };

  const renderNotesFilterMenuItem = () => {
    if (!notesColumn) return null;

    const isActive = isFilterActive('userNoteCount');
    const value: NoteCountFilterValue =
      notesColumn.filterValue === undefined ? null : notesColumn.filterValue;

    return (
      <B.MenuItem
        key="notes"
        text="Notes"
        icon={isActive ? 'filter' : 'blank'}
        active={isActive}
        shouldDismissPopover={false}
      >
        <List
          className={csFilters.actionMenu}
          items={noteCountFilterListItems(value)}
          onClickItem={({id}) => {
            table.setFilter(notesColumn.id, mapNoteCountMenuIdToFilterValue(id));
          }}
        />
      </B.MenuItem>
    );
  };

  const renderAlertFilterMenuItem = () => {
    if (!alertColumn) return null;

    const isActive = isFilterActive('alertNoteCount');
    const value = decodeAlertCountFilterValue(alertColumn.filterValue);

    return (
      <B.MenuItem
        key="alerts"
        text="Lookouts"
        icon={isActive ? 'filter' : 'blank'}
        active={isActive}
        shouldDismissPopover={false}
      >
        <List
          className={csFilters.actionMenu}
          items={
            [
              ...getAlertListItems(
                'none',
                'none',
                'none',
                selectedFeatureCollection,
                alertPolicies,
                I.List([]),
                featureFlags.PARCEL_CHANGE_ALERTS(organization)
              ).map((item) => ({
                id: item.id,
                text: item.text,
                icon: value.has(item.id) ? 'tick' : 'blank',
              })),
              {id: 'sep1', text: '', isSeparator: true, icon: 'blank'},
              {id: 'reset', text: 'Reset filter', icon: 'blank'},
            ] as ListItem<{id: string}>[]
          }
          onClickItem={({id}, ev) => {
            ev.preventDefault();
            if (id === 'reset') {
              table.setFilter(alertColumn.id, undefined);
            } else {
              const isSelected = value.has(id);
              if (isSelected) {
                table.setFilter(alertColumn.id, encodeAlertCountFilterValue(value.remove(id)));
              } else {
                table.setFilter(alertColumn.id, encodeAlertCountFilterValue(value.add(id)));
              }
            }
          }}
        />
      </B.MenuItem>
    );
  };

  const renderAssigneeFilterMenuItem = () => {
    if (!assigneeColumn) return null;

    const isActive = isFilterActive('assignee');
    const value: string | null =
      assigneeColumn.filterValue === undefined ? null : assigneeColumn.filterValue;

    return (
      <B.MenuItem
        key="assignee"
        text="Assignee"
        icon={isActive ? 'filter' : 'blank'}
        active={isActive}
        shouldDismissPopover={false}
      >
        <List
          className={csFilters.actionMenu}
          items={[
            ...assigneeFilterBaseListItems(value),
            ...makeUserListItems(organizationUsers, (u) => value === u.email),
          ]}
          onClickItem={({id}) => {
            table.setFilter(assigneeColumn.id, mapAllAnyNoneMenuIdToFilterValue(id));
          }}
        />
      </B.MenuItem>
    );
  };

  const renderDateFilterMenuItem = () => {
    if (!dateColumn) return null;

    const isActive = isFilterActive('reportingDueDate');
    const value: string | null =
      dateColumn.filterValue === undefined ? null : dateColumn.filterValue;

    return (
      <B.MenuItem
        key="date"
        text="Due Date"
        icon={isActive ? 'filter' : 'blank'}
        active={isActive}
        shouldDismissPopover={false}
      >
        <List
          className={csFilters.actionMenu}
          items={dateFilterListItems(value)}
          onClickItem={({id}) => {
            table.setFilter(dateColumn.id, mapAllAnyNoneMenuIdToFilterValue(id));
          }}
        />
      </B.MenuItem>
    );
  };

  const renderAdditionalFeaturePropertyFilterMenuItems = () => {
    // for each additional custom column, render a filter menu item that mimics the regular column filter menuitem
    return additionalFeaturePropertyColumns.map((col) => {
      return (
        <B.MenuItem
          key={col.id}
          text={col.id}
          icon={isFilterActive(col.id!) ? 'filter' : 'blank'}
          active={isFilterActive(col.id!)}
          shouldDismissPopover={false}
        >
          <AdditionalFeaturePropertyFilterMenuBody column={col} table={table} />
        </B.MenuItem>
      );
    });
  };

  const menuContent = (
    <B.Menu>
      {renderTagsFilterMenuItem()}
      {renderImageryFilterMenuItem()}
      {renderNotesFilterMenuItem()}
      {renderAlertFilterMenuItem()}
      {renderAssigneeFilterMenuItem()}
      {renderDateFilterMenuItem()}
      {renderAdditionalFeaturePropertyFilterMenuItems()}
    </B.Menu>
  );

  return (
    <B.ButtonGroup>
      <B.Popover content={menuContent} position={B.Position.BOTTOM_LEFT}>
        <ActiveFiltersCountButton count={activeFilterCount} outlined />
      </B.Popover>
      {activeFilterCount > 0 && (
        <B.Tooltip content="Clear all filters">
          <B.Button icon="filter-remove" outlined onClick={() => table.setAllFilters([])} />
        </B.Tooltip>
      )}
    </B.ButtonGroup>
  );
};

export default PropertyOverviewFiltersMenu;
